import type { Map as MaplibreMap } from "maplibre-gl";
import type { GeoJSON } from "geojson";

export type LassoAction = "reset" | "append" | "subtract";
export type LassoSelectMode = "intersects" | "contains";

export interface LassoSelectResult {
	polygon: GeoJSON.Polygon;
	features: GeoJSON.Feature[];
	action: LassoAction;
}

type LngLatLike = { lng: number; lat: number };

/** Close the sampled drag points into a GeoJSON polygon; null if too few points. */
export function ringToPolygon(pts: LngLatLike[]): GeoJSON.Polygon | null {
	if (pts.length < 3) return null;
	const ring: [number, number][] = pts.map((p) => [p.lng, p.lat]);
	// Close the ring so fill/outline render as a closed shape.
	ring.push([...ring[0]]);
	return { type: "Polygon", coordinates: [ring] };
}

/**
 * Ray-casting point-in-polygon test against a closed ring.
 * Works for any ring, including concave and self-intersecting ones.
 */
export function pointInRing(
	lng: number,
	lat: number,
	ring: [number, number][],
): boolean {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i];
		const [xj, yj] = ring[j];
		const crosses =
			yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
		if (crosses) inside = !inside;
	}
	return inside;
}

/** Every coordinate of a geometry, flattened. */
function coordsOf(geometry: GeoJSON.Geometry): [number, number][] {
	switch (geometry.type) {
		case "Point":
			return [geometry.coordinates as [number, number]];
		case "MultiPoint":
			return geometry.coordinates as [number, number][];
		case "LineString":
			return geometry.coordinates as [number, number][];
		case "MultiLineString":
			return geometry.coordinates.flat() as [number, number][];
		case "Polygon":
			return geometry.coordinates.flat() as [number, number][];
		case "MultiPolygon":
			return geometry.coordinates.flat(2) as [number, number][];
		case "GeometryCollection":
			return geometry.geometries.flatMap((g) => coordsOf(g));
	}
}

/**
 * Selection predicate for a feature against the lasso polygon.
 *
 * Point features are exact (ray cast). For lines/polygons this is a
 * vertex-based approximation: "intersects" = any vertex inside the lasso,
 * "contains" = every vertex inside. For point data (the primary use case)
 * both modes are exact and equivalent.
 */
export function featureInPolygon(
	polygon: GeoJSON.Polygon,
	feature: GeoJSON.Feature,
	mode: LassoSelectMode,
): boolean {
	if (!feature.geometry) return false;
	const ring = polygon.coordinates[0] as [number, number][];
	const coords = coordsOf(feature.geometry);
	if (coords.length === 0) return false;
	if (mode === "contains") {
		return coords.every(([lng, lat]) => pointInRing(lng, lat, ring));
	}
	return coords.some(([lng, lat]) => pointInRing(lng, lat, ring));
}

/**
 * Screen-space bounding box of a lng/lat ring, as the two-point geometry
 * `queryRenderedFeatures` accepts. Used to cheaply narrow candidates before
 * the exact `featureInPolygon` pass.
 */
export function bboxFromRing(
	m: MaplibreMap,
	ring: [number, number][],
): [[number, number], [number, number]] {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const [lng, lat] of ring) {
		const p = m.project([lng, lat]);
		minX = Math.min(minX, p.x);
		minY = Math.min(minY, p.y);
		maxX = Math.max(maxX, p.x);
		maxY = Math.max(maxY, p.y);
	}
	return [
		[minX, minY],
		[maxX, maxY],
	];
}
